import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import Button from '@components/common/Button';
import { Checkbox } from '@components/common';
import styles from './DivisionFilterDropdown.module.css';

interface DivisionOption {
  id: number;
  name: string;
}

interface DivisionFilterDropdownProps {
  divisions: DivisionOption[];
  /** Empty means All divisions. */
  selectedDivisionIds: number[];
  disabled?: boolean;
  onSelectionChange: (divisionIds: number[]) => void;
}

/**
 * The Dashboard Division filter: narrows every widget to the chosen Divisions. It is saved with the user's
 * Dashboard. With nothing chosen the Dashboard shows program-wide data (All divisions).
 */
const DivisionFilterDropdown: React.FC<DivisionFilterDropdownProps> = ({
  divisions,
  selectedDivisionIds,
  disabled = false,
  onSelectionChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDivision = (divisionId: number) => {
    if (selectedDivisionIds.includes(divisionId)) {
      onSelectionChange(selectedDivisionIds.filter((id) => id !== divisionId));
    } else {
      onSelectionChange([...selectedDivisionIds, divisionId]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.dropdown} ref={dropdownRef} data-test-id="dashboard-division-filter">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        dataTestId="dashboard-division-filter-toggle"
      >
        {selectedDivisionIds.length > 0 ? `Divisions (${selectedDivisionIds.length})` : 'All divisions'}
        <ChevronDown size={16} className={styles.icon} />
      </Button>

      {isOpen && (
        <div className={styles.menu} data-test-id="dashboard-division-filter-menu">
          <div className={styles.menuHeader}>
            <span>Select Divisions</span>
            {selectedDivisionIds.length > 0 && (
              <button type="button" onClick={() => onSelectionChange([])} className={styles.clear}>
                Show all
              </button>
            )}
          </div>
          <div className={styles.menuContent}>
            {divisions.map((division) => (
              <div key={division.id} className={styles.option}>
                <Checkbox
                  checked={selectedDivisionIds.includes(division.id)}
                  onChange={() => toggleDivision(division.id)}
                  label={division.name}
                  wrapperClassName={styles.checkboxWrapper}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { DivisionFilterDropdown };
